import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader } from '@edgebox/react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalId } from '@edgebox/data-definition-kit';
import {
  AccessScope,
  AiModelUseCase,
  AiPromptOutputFormat,
  AiPromptTemplateStatus,
  SyncCoreAiPromptOutput,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientAiModelEntity,
  ClientAiPromptTemplateDraft,
  ClientAiPromptTemplateEntity,
  ClientAiProviderEntity,
} from '@edgebox/sync-core-rest-client';
import { formatDateTime, renderValue, useSyncCoreApi } from '../aiHelpers';
import { DetailsCard } from '../components/DetailsCard';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { RawDataCard } from '../components/RawDataCard';
import { ScreenHeader } from '../components/ScreenHeader';
import { AiModelName } from '../components/AiModelName';
import { AiProviderName } from '../components/AiProviderName';
import {
  AiPromptTemplateDraftEditor,
  AiPromptTemplateEntityEditor,
  ensurePromptTemplateDraftDefaults,
  ensurePromptTemplateEntityDefaults,
} from '../components/AiPromptTemplateForm';

function isNewId(id: string | undefined): boolean {
  return !id || id === 'new';
}

function createDefaultPromptTemplateDraft(): ClientAiPromptTemplateDraft {
  const draft = new ClientAiPromptTemplateDraft();
  draft.accessScope = AccessScope.Project;
  draft.status = AiPromptTemplateStatus.Draft;
  draft.name = '';
  draft.useCase = AiModelUseCase.Reasoning;
  draft.key = '';
  draft.version = 1;
  draft.variables = [];
  draft.userMessage = '';
  const output = new SyncCoreAiPromptOutput();
  output.format = AiPromptOutputFormat.Text;
  draft.output = output;
  draft.tokensMax = 1000;
  draft.defaultParameters = {};
  return draft;
}

export function AiPromptTemplateDetailView() {
  const api = useSyncCoreApi();
  const params = useParams();
  const navigate = useNavigate();
  const idParam = params.id;

  const creating = isNewId(idParam);
  const id = !creating ? (idParam as InternalId) : undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [entity, setEntity] = useState<ClientAiPromptTemplateEntity | undefined>(undefined);
  const [provider, setProvider] = useState<ClientAiProviderEntity | undefined>(undefined);
  const [model, setModel] = useState<ClientAiModelEntity | undefined>(undefined);
  const [showEdit, setShowEdit] = useState<boolean>(creating);

  const draft = useMemo(() => (creating ? createDefaultPromptTemplateDraft() : undefined), [creating]);

  useEffect(() => {
    if (!api || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.promptTemplates
      .item(id)
      .then(async (e) => {
        if (cancelled) return;
        ensurePromptTemplateEntityDefaults(e);
        setEntity(e);

        try {
          const [p, m] = await Promise.all([e.provider.get(), e.model.get()]);
          if (!cancelled) {
            setProvider(p);
            setModel(m);
          }
        } catch {
          // optional
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  if (!api) return <Alert variant="warning">Missing API.</Alert>;
  if (!creating && !id) return <Alert variant="warning">Missing prompt template id.</Alert>;

  return (
    <>
      <ScreenHeader
        title={creating ? 'Create prompt template' : 'Prompt template details'}
        onBack={() => navigate('/ai/prompt-template')}
        right={
          !creating && entity ? (
            <Button variant={showEdit ? 'light' : 'primary'} onClick={() => setShowEdit((v) => !v)}>
              {showEdit ? 'Cancel edit' : 'Edit'}
            </Button>
          ) : undefined
        }
      />

      {loading ? <LoadingCard text="Loading prompt template…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      {creating && draft ? (
        <Card className="mb-3">
          <CardHeader>Prompt template</CardHeader>
          <CardBody>
            {(() => {
              ensurePromptTemplateDraftDefaults(draft);
              return (
                <AiPromptTemplateDraftEditor
                  api={api}
                  initialValues={draft}
                  submitLabel="Create"
                  onSubmit={async (values) => {
                    const created = await api.ai.promptTemplates.create(values);
                    setEntity(created);
                    navigate(`/ai/prompt-template/${created.id}`, { replace: true });
                  }}
                />
              );
            })()}
          </CardBody>
        </Card>
      ) : null}

      {!creating && entity ? (
        <>
          {!showEdit ? (
            <>
              <DetailsCard
                title="Overview"
                fields={[
                  { label: 'ID', value: renderValue(entity.id) },
                  { label: 'Name', value: renderValue(entity.name) },
                  { label: 'Key', value: renderValue(entity.key) },
                  {
                    label: 'Status',
                    value: <Badge bg={entity.status === AiPromptTemplateStatus.Active ? 'success' : 'secondary'}>{entity.status}</Badge>,
                  },
                  { label: 'Access scope', value: renderValue(entity.accessScope) },
                  { label: 'Frozen', value: renderValue(entity.frozen) },
                  {
                    label: 'Provider',
                    value: provider ? renderValue(provider.name) : <AiProviderName id={entity.provider?.getId?.()} />,
                  },
                  {
                    label: 'Model',
                    value: model ? renderValue(model.name) : <AiModelName id={entity.model?.getId?.()} />,
                  },
                  { label: 'Use case', value: renderValue(entity.useCase) },
                  { label: 'Version', value: renderValue(entity.version) },
                  { label: 'Customer', value: renderValue(entity.customer?.getId?.()) },
                  { label: 'Project', value: renderValue(entity.project?.getId?.()) },
                  { label: 'Created', value: renderValue(formatDateTime(entity.createdAt)) },
                  { label: 'Updated', value: renderValue(formatDateTime(entity.updatedAt)) },
                ]}
              />

              <DetailsCard
                title="Prompt settings"
                fields={[
                  { label: 'Service prompt', value: renderValue(entity.servicePrompt) },
                  { label: 'Output format', value: renderValue(entity.output?.format) },
                  { label: 'Text format', value: renderValue(entity.output?.textFormat) },
                  { label: 'Max tokens', value: renderValue(entity.tokensMax) },
                  { label: 'Min tokens', value: renderValue(entity.tokensMin) },
                  { label: 'Min costs', value: renderValue(entity.costsMin) },
                  { label: 'Max costs', value: renderValue(entity.costsMax) },
                ]}
              />

              <RawDataCard entity={entity} />
            </>
          ) : (
            <Card className="mb-3">
              <CardHeader>Edit prompt template</CardHeader>
              <CardBody>
                <AiPromptTemplateEntityEditor
                  api={api}
                  initialValues={entity}
                  submitLabel="Save"
                  onSubmit={async (values) => {
                    const updated = await api.ai.promptTemplates.update(values);
                    setEntity(updated);
                    setShowEdit(false);
                  }}
                />
              </CardBody>
            </Card>
          )}
        </>
      ) : null}
    </>
  );
}
